/**
 * Undo/redo key bindings — registered on the ONE shortcut registry
 * (src/ui/keyboard.ts), never a second window listener.
 *
 *   Ctrl/Cmd-Z          undo   ·  Ctrl/Cmd-Shift-Z, Ctrl/Cmd-Y  redo
 *
 * Each registration sets `guardTyping: true`, the registry's opt-in form of
 * its existing typing guard: while focus sits in a text field the keydown is
 * left entirely to the field (no preventDefault), because there the browser's
 * NATIVE undo is what the GM wants; outside a field the combo fires the
 * store's undo/redo actions.
 *
 * Deliberately its own module rather than inline in App.tsx: the Node gate
 * `tools/smoke-history.ts` imports these REAL handlers, installs the registry
 * against a fake window and dispatches synthetic keydowns — combos, handlers
 * and the typing guard are exercised exactly as the app wires them. What a
 * Node harness cannot see — a real key event from OS hardware arriving in a
 * live page — is stated as browser-only in that gate's header.
 */
import { redoCampaignEdit, undoCampaignEdit } from '@/state/store';
import { registerShortcut } from '@/ui/keyboard';

/** Register the three combos; call once from the app shell, dispose on cleanup. */
export function registerHistoryShortcuts(): () => void {
  const offs = [
    registerShortcut({
      combo: 'ctrl+z',
      descriptionKey: 'shortcuts.undo',
      guardTyping: true,
      handler: () => {
        undoCampaignEdit();
      },
    }),
    registerShortcut({
      combo: 'ctrl+shift+z',
      descriptionKey: 'shortcuts.redo',
      guardTyping: true,
      handler: () => {
        redoCampaignEdit();
      },
    }),
    registerShortcut({
      combo: 'ctrl+y',
      descriptionKey: 'shortcuts.redo',
      guardTyping: true,
      handler: () => {
        redoCampaignEdit();
      },
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}
